/** Fixed marketplace reward triggers (ids must match backend marketplaceSettings.js). */

export const MARKETPLACE_REWARD_TRIGGERS = [
  {
    id: "login",
    label: "Daily login",
    description: "Award once per calendar day when the user starts a session.",
  },
  {
    id: "projectComment",
    label: "Post a project comment",
    description: "Award when the user adds a comment on a project (up to 5 times per day).",
  },
  {
    id: "fieldFileUpload",
    label: "Upload a project file",
    description:
      "Award when a file is uploaded and saved on a project field. Replacing with a newer file (different content) can earn again; re-uploading identical bytes does not. Removing the file reverses the award.",
  },
  {
    id: "contractCreate",
    label: "Add a contract",
    description: "Award when the user creates a new contract.",
  },
  {
    id: "contractAward",
    label: "Award a contract",
    description:
      "Award the user who changes a contract status from Pending Award to Awarded (once per contract).",
  },
  {
    id: "contractStatusUpdate",
    label: "Update contract status",
    description:
      "Award the user who changes a contract status (for transitions other than Pending Award → Awarded).",
  },
  {
    id: "phaseComplete_sales",
    label: "Phase complete — Sales",
    description:
      "Award the project sales rep plus active users in the eligible roles you select below when Sales is marked complete.",
    phaseComplete: true,
  },
  {
    id: "phaseComplete_finance",
    label: "Phase complete — Finance",
    description:
      "Award the project sales rep plus active users in the eligible roles you select below when Finance is marked complete.",
    phaseComplete: true,
  },
  {
    id: "phaseComplete_drafting",
    label: "Phase complete — Drafting",
    description:
      "Award the project drafter plus active users in the eligible roles you select below when Drafting is marked complete.",
    phaseComplete: true,
  },
  {
    id: "phaseComplete_permitting",
    label: "Phase complete — Permitting",
    description:
      "Award active users in the eligible roles you select below when Permitting is marked complete.",
    phaseComplete: true,
  },
  {
    id: "phaseComplete_construction",
    label: "Phase complete — Construction",
    description:
      "Award project construction assignees plus active users in the eligible roles you select below when Construction is marked complete.",
    phaseComplete: true,
  },
  {
    id: "phaseComplete_fiber",
    label: "Phase complete — Fiber",
    description:
      "Award the project splicer plus active users in the eligible roles you select below when Fiber is marked complete.",
    phaseComplete: true,
  },
  {
    id: "phaseComplete_networking",
    label: "Phase complete — Networking",
    description:
      "Award assigned technicians plus active users in the eligible roles you select below when Networking is marked complete.",
    phaseComplete: true,
  },
  {
    id: "phaseComplete_accounting",
    label: "Phase complete — Accounting",
    description:
      "Award the project sales rep plus active users in the eligible roles you select below when Accounting is marked complete.",
    phaseComplete: true,
  },
];

/** Suggested role-name patterns when eligibleRoleTypeIds has not been saved yet. */
export const DEFAULT_PHASE_COMPLETE_ELIGIBLE_ROLE_NAMES = {
  phaseComplete_sales: ["sales representative", "sales rep", "sales manager"],
  phaseComplete_finance: ["sales representative", "sales rep", "sales manager"],
  phaseComplete_drafting: ["drafter", "osp engineer", "engineering manager"],
  phaseComplete_permitting: [
    "permitting manager",
    "permitting specialist",
    "permitting specalist",
    "coordinator",
    "project manager",
  ],
  phaseComplete_construction: [
    "construction manager",
    "construction laborer",
    "construction lead",
    "inspector",
  ],
  phaseComplete_fiber: ["splicer", "splicer manager"],
  phaseComplete_networking: ["network manager", "network technician"],
  phaseComplete_accounting: ["sales representative", "sales rep"],
};

export const DEFAULT_MARKETPLACE_REWARD_AMOUNTS = {
  login: 3,
  projectComment: 5,
  fieldFileUpload: 8,
  contractCreate: 25,
  contractAward: 25,
  contractStatusUpdate: 15,
  phaseComplete_sales: 15,
  phaseComplete_finance: 18,
  phaseComplete_drafting: 28,
  phaseComplete_permitting: 22,
  phaseComplete_construction: 35,
  phaseComplete_fiber: 32,
  phaseComplete_networking: 32,
  phaseComplete_accounting: 25,
};

function normalizeMatrixRoleName(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function roleNameMatchesPatterns(name, patterns) {
  const n = normalizeMatrixRoleName(name);
  if (!n) return false;
  return patterns.some((pattern) => {
    const p = normalizeMatrixRoleName(pattern);
    return n === p || n.includes(p);
  });
}

export function roleTypeIdsMatchingNamePatterns(matrix, patterns) {
  const types = Array.isArray(matrix?.userTypes) ? matrix.userTypes : [];
  const ids = [];
  for (const t of types) {
    if (!t?.id) continue;
    if (roleNameMatchesPatterns(t.name, patterns)) ids.push(String(t.id).trim());
  }
  return [...new Set(ids.filter(Boolean))];
}

export function resolvePhaseCompleteEligibleRoleTypeIds(triggerId, matrix, settings) {
  const tr = settings?.rewards?.[triggerId];
  if (tr && Array.isArray(tr.eligibleRoleTypeIds)) {
    return tr.eligibleRoleTypeIds;
  }
  return roleTypeIdsMatchingNamePatterns(
    matrix,
    DEFAULT_PHASE_COMPLETE_ELIGIBLE_ROLE_NAMES[triggerId] || []
  );
}

function normalizeEligibleRoleTypeIds(raw) {
  if (!Array.isArray(raw)) return undefined;
  return [...new Set(raw.map((id) => String(id || "").trim()).filter(Boolean))].sort();
}

export function defaultMarketplaceSettings() {
  const rewards = {};
  for (const def of MARKETPLACE_REWARD_TRIGGERS) {
    rewards[def.id] = {
      enabled: true,
      amount: DEFAULT_MARKETPLACE_REWARD_AMOUNTS[def.id] ?? 0,
    };
  }
  return { rewards };
}

export function normalizeMarketplaceSettings(raw) {
  const base = defaultMarketplaceSettings();
  if (!raw || typeof raw !== "object") return base;
  const rewards = { ...base.rewards };
  if (raw.rewards && typeof raw.rewards === "object") {
    for (const def of MARKETPLACE_REWARD_TRIGGERS) {
      const tr = raw.rewards[def.id];
      if (!tr || typeof tr !== "object") continue;
      const next = {
        enabled: tr.enabled !== false,
        amount: Math.max(0, Math.min(100000, Math.trunc(Number(tr.amount) || 0))),
      };
      if (def.phaseComplete) {
        const eligibleRoleTypeIds = normalizeEligibleRoleTypeIds(tr.eligibleRoleTypeIds);
        if (eligibleRoleTypeIds !== undefined) {
          next.eligibleRoleTypeIds = eligibleRoleTypeIds;
        }
      }
      rewards[def.id] = next;
    }
  }
  return { rewards };
}
